import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORE_NAMES = ['Amazon', 'Walmart', 'Target', 'CVS', 'Starbucks', 'Shell', 'McDonalds', 'BestBuy', 'HomeDepot', 'Costco'];

export const useDashboardData = () => {
  const [stats, setStats] = useState(null);
  const [blockedByReason, setBlockedByReason] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeTransactions, setStoreTransactions] = useState(
    STORE_NAMES.map(name => ({ name, transactions: 0, blocked: 0 }))
  );

  const fetchDashboardStats = useCallback(async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const timestamp = Date.now();
      const [statsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats?_t=${timestamp}`),
        axios.get(`${API}/analytics/blocked-by-reason?_t=${timestamp}`),
      ]);
      setStats(statsRes.data);
      setBlockedByReason(blockedRes.data.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching dashboard data:", error);
      }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStoreData = useCallback(async () => {
    try {
      const timestamp = Date.now();
      const response = await axios.get(`${API}/transactions?limit=100&_t=${timestamp}`);
      if (response.data && Array.isArray(response.data)) {
        const storeCounts = {};
        const blockedCounts = {};
        
        STORE_NAMES.forEach(name => {
          storeCounts[name] = 0;
          blockedCounts[name] = 0;
        });
        
        response.data.forEach(txn => {
          const matchedStore = STORE_NAMES.find(store => 
            txn.merchant?.toLowerCase().includes(store.toLowerCase()) ||
            store.toLowerCase().includes(txn.merchant?.toLowerCase().split(' ')[0] || '')
          );
          
          if (matchedStore) {
            storeCounts[matchedStore]++;
            if (txn.status === 'blocked') {
              blockedCounts[matchedStore]++;
            }
          } else {
            const randomStore = STORE_NAMES[Math.floor(Math.random() * STORE_NAMES.length)];
            storeCounts[randomStore]++;
            if (txn.status === 'blocked') {
              blockedCounts[randomStore]++;
            }
          }
        });
        
        setStoreTransactions(
          STORE_NAMES.map(name => ({
            name,
            transactions: storeCounts[name],
            blocked: blockedCounts[name]
          }))
        );
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching store data:", error);
      }
    }
  }, []);

  const updateStatsFromTransaction = useCallback((message) => {
    // Update store transaction counts
    setStoreTransactions(prev => {
      const newData = [...prev];
      const merchant = message.data.merchant || '';
      
      let storeIndex = STORE_NAMES.findIndex(store => 
        merchant.toLowerCase().includes(store.toLowerCase())
      );
      
      if (storeIndex === -1) {
        storeIndex = Math.floor(Math.random() * STORE_NAMES.length);
      }
      
      newData[storeIndex] = {
        ...newData[storeIndex],
        transactions: newData[storeIndex].transactions + 1,
        blocked: message.data.status === 'blocked' 
          ? newData[storeIndex].blocked + 1 
          : newData[storeIndex].blocked
      };
      
      return newData;
    });
    
    // Update stats
    setStats(prev => prev ? {
      ...prev,
      total_transactions: prev.total_transactions + 1,
      fraud_detected: message.data.status !== 'approved' ? prev.fraud_detected + 1 : prev.fraud_detected,
      total_blocked: message.data.status === 'blocked' ? prev.total_blocked + 1 : prev.total_blocked
    } : prev);
  }, []);

  return {
    stats,
    blockedByReason,
    loading,
    storeTransactions,
    fetchDashboardStats,
    fetchStoreData,
    updateStatsFromTransaction,
    STORE_NAMES
  };
};
